import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import storage from '../../../../hooks/useStorage/useStorage';
import LangEnum from '../../../../lang/LangEnum';
import { OrderType } from '../../../orders/interfaces/orderType.enum';
import { AvailabilityEnum } from '../../../products/ProductTypes';
import {
  isPreOrder,
  transformAvailabilityToPreOrder,
} from '../../../products/utils/availability.utils';
import { OfflineDatabaseCustomer } from '../../database/offlineDatabase/types/offlineDatabaseCustomer.types';
import {
  OfflineDatabaseOrder,
  OfflineDatabaseOrderProduct,
} from '../../database/offlineDatabase/types/offlineDatabaseOrder.types';
import { OfflineDatabasePriceTable } from '../../database/offlineDatabase/types/offlineDatabasePriceTable.types';
import { OfflineDatabaseSalesRepresentative } from '../../database/offlineDatabase/types/offlineDatabaseSalesRepresentative.types';
import { OfflineDatabaseSeller } from '../../database/offlineDatabase/types/offlineDatabaseSeller.types';
import {
  AddProductParams,
  ChangeProductGridBasedOnPacksParams,
  ChangeProductGridParams,
  CommonParamsWithOnlineOrder,
  GetOrderProductParams,
  OfflineOrderCreateParams,
  OfflineOrderMetadata,
  OfflineOrderOnlineMetadata,
  OfflineOrderStaticCreateParams,
  OfflineStatus,
  SetPriceTableAdditionalData,
} from './offlineOrder.types';
import { OfflineOrderProduct } from './orderProduct/OfflineOrderProduct';
import { OfflineOrderProductStaticCreateParams } from './orderProduct/offlineOrderProduct.types';
import { OfflineOrderProductAlreadyExists } from './utils/errors/OfflineOrderProductAlreadyExists.error';
import { OfflineOrderValidator } from './validator/OfflineOrder.validator';
import { OfflineOrderRulesValidatorFactory } from './validator/OfflineOrderValidator.factory';

export class OfflineOrder implements CommonParamsWithOnlineOrder {
  private _status = OfflineStatus.OFFLINE;

  private _id: string;
  private _createdAt: Date;
  private _updatedAt: Date;
  private _brandId: string;
  private _priceTable: OfflineDatabasePriceTable;

  private _code?: number;
  private _observation?: string;
  private _seller?: OfflineDatabaseSeller;
  private _products?: OfflineOrderProduct[];
  private _customer?: OfflineDatabaseCustomer;
  private _salesRepresentative?: OfflineDatabaseSalesRepresentative;

  private _metadata?: OfflineOrderMetadata;

  private constructor({
    id,
    code,
    seller,
    status,
    brandId,
    customer,
    products,
    createdAt,
    updatedAt,
    priceTable,
    observation,
    salesRepresentative,
    metadata,
  }: OfflineOrderCreateParams) {
    this._id = id;
    this._code = code;
    this._status = status;
    this._seller = seller;
    this._brandId = brandId;
    this._customer = customer;
    this._products = products;
    this._metadata = metadata;
    this._createdAt = createdAt;
    this._updatedAt = updatedAt;
    this._priceTable = priceTable;
    this._observation = observation;
    this._salesRepresentative = salesRepresentative;
  }

  static create({
    seller,
    customer,
    priceTable,
    salesRepresentative,
  }: OfflineOrderStaticCreateParams) {
    const orderId = uuidv4();

    return new OfflineOrder({
      seller,
      customer,
      priceTable,
      id: orderId,
      salesRepresentative,
      createdAt: new Date(),
      updatedAt: new Date(),
      brandId: storage.getBrandId(),
      status: OfflineStatus.OFFLINE,
    });
  }

  static fromOfflineDatabase(savedOrder: OfflineDatabaseOrder) {
    const products = savedOrder.products?.map((product) =>
      OfflineOrderProduct.fromOfflineDatabase(product),
    );

    const order = new OfflineOrder({
      ...savedOrder,
      products,
      updatedAt: savedOrder.updatedAt || savedOrder.createdAt,
    });

    return order;
  }

  setObservation(observation: string) {
    this._observation = observation;

    this.updateUpdatedAt();
  }

  setCustomer(customer?: OfflineDatabaseCustomer) {
    this._customer = customer;

    this.updateUpdatedAt();
  }

  setSeller(seller?: OfflineDatabaseSeller) {
    this._seller = seller;

    this.updateUpdatedAt();
  }

  setSalesRepresentative(salesRepresentative?: OfflineDatabaseSalesRepresentative) {
    this._salesRepresentative = salesRepresentative;

    this.updateUpdatedAt();
  }

  setPriceTable(
    priceTable: OfflineDatabasePriceTable,
    { productsPriceInfo }: SetPriceTableAdditionalData = { productsPriceInfo: [] },
  ) {
    if (!priceTable) {
      return;
    }

    const validator = new OfflineOrderValidator(new OfflineOrderRulesValidatorFactory());

    this._priceTable = priceTable;

    productsPriceInfo.forEach((productPriceInfo) => {
      const products = this._products?.filter(
        (product) => product.productColor.id === productPriceInfo.productColorId,
      );

      if (products?.length) {
        products.forEach((product) => product.changePrice(productPriceInfo));
      }
    });

    validator.validateOrThrow(this, {
      useRulesTo: 'CHANGE_PRICE_TABLE',
    });

    this.updateUpdatedAt();
  }

  startSyncing(onlineMetadata: OfflineOrderOnlineMetadata) {
    if (!this._metadata) {
      this._metadata = {};
    }

    this._status = OfflineStatus.SYNCING;
    this._metadata.errorMetadata = undefined;
    this._metadata.onlineMetadata = onlineMetadata;

    this.updateUpdatedAt();
  }

  errorSyncing(message?: LangEnum) {
    this._status = OfflineStatus.ERROR_SYNCING;

    if (!this._metadata) {
      this._metadata = {};
    }

    this._metadata.errorMetadata = {
      errorMessage: message,
      lastSyncingError: new Date(),
    };

    if (this._metadata?.onlineMetadata) {
      this._metadata.onlineMetadata = undefined;
    }

    this.updateUpdatedAt();
  }

  addProduct({
    kit,
    grid,
    product,
    productColor,
    availability,
    deliveryWindow,
    productCategory,
    productCollection,
    packsWithMultiplier,
    productColorPriceInfo,
    productColorClassifications,
  }: AddProductParams) {
    const existentOrderProduct = this.getOrderProduct({
      availability,
      productColorId: productColor.id,
      deliveryWindowId: deliveryWindow?.id,
    });

    if (existentOrderProduct) {
      throw new OfflineOrderProductAlreadyExists();
    }

    const commonParams: OfflineOrderProductStaticCreateParams = {
      kit,
      grid,
      product,
      availability,
      productColor,
      deliveryWindow,
      metadata: {
        productCategory,
        productCollection,
        productColorPriceInfo,
        productColorClassifications,
      },
    };

    const orderProduct = packsWithMultiplier?.length
      ? OfflineOrderProduct.createWithPacks({
          ...commonParams,
          packsWithMultiplier,
        })
      : OfflineOrderProduct.create(commonParams);

    this._products = this._products ? [orderProduct, ...this._products] : [orderProduct];

    this.updateUpdatedAt();
  }

  removeOrderProducts(orderProducts: OfflineOrderProduct[]) {
    this._products = this._products?.filter((product) => !orderProducts.includes(product));

    this.updateUpdatedAt();
  }

  changeProductGrid({ gridValues, orderProductIds }: ChangeProductGridParams) {
    const orderProductToChange = this.getOrderProduct(orderProductIds);

    if (!orderProductToChange) {
      throw new Error('product not found');
    }

    orderProductToChange.changeGrid(gridValues);

    this.updateUpdatedAt();
  }

  changeProductGridBasedOnPacks({
    orderProductIds,
    packsWithMultiplier,
    productColorWithAllPacks,
  }: ChangeProductGridBasedOnPacksParams) {
    const orderProductToChange = this.getOrderProduct(orderProductIds);

    if (!orderProductToChange) {
      throw new Error('product not found');
    }

    orderProductToChange.changeGridBasedOnPacks({
      packsWithMultiplier,
      productColorWithAllPacks,
    });

    this.updateUpdatedAt();
  }

  createProductCopyGridAndReplace(
    orderProductToChange: OfflineOrderProduct,
    {
      grid,
      product,
      productColor,
      availability,
      deliveryWindow,
      productCategory,
      productCollection,
      packsWithMultiplier,
      productColorPriceInfo,
      productColorClassifications,
    }: AddProductParams,
  ) {
    const existentOrderProduct = this.getOrderProduct({
      availability,
      productColorId: productColor.id,
      deliveryWindowId: deliveryWindow?.id,
    });

    if (existentOrderProduct) {
      throw new Error('product already exists');
    }

    const orderProduct = OfflineOrderProduct.create({
      grid,
      product,
      availability,
      productColor,
      deliveryWindow,
      metadata: {
        productCategory,
        productCollection,
        productColorPriceInfo,
        productColorClassifications,
      },
    });

    if (packsWithMultiplier) {
      orderProduct.copyGridWithPacks({
        packsWithMultiplier,
        productColorWithAllPacks: productColor,
      });
    } else {
      orderProduct.copyGrid(orderProductToChange.grid);
    }

    this.replaceProductToOther(orderProductToChange, orderProduct);

    this.updateUpdatedAt();
  }

  getOrderProduct({ availability, deliveryWindowId, productColorId }: GetOrderProductParams) {
    return this._products?.find(
      (product) =>
        product.availability === availability &&
        product.productColor.id === productColorId &&
        product.deliveryWindow?.id === deliveryWindowId,
    );
  }

  getOrderProductsFromProductColorId(productColorId: string) {
    return this._products?.filter((product) => product.productColor.id === productColorId) ?? [];
  }

  getOrderProductsFromProductColorIds(productColorIds: ReadonlyArray<string>) {
    return (
      this._products?.filter((product) => productColorIds.includes(product.productColor.id)) ?? []
    );
  }

  getOrderProductByGridItemId(gridItemId: string) {
    return this._products?.find((product) =>
      product.grid.some((gridItem) => gridItem.id === gridItemId),
    );
  }

  getOrderProductCombinationsMap() {
    const combinationsMap = new Map<string, OfflineDatabaseOrderProduct>();

    this._products?.forEach((product) => {
      const combinationKey = `${product.productColor.id}|${product.availability}|${
        product.deliveryWindow?.id || 'no-delivery-window'
      }`;

      if (!combinationsMap.has(combinationKey)) {
        combinationsMap.set(combinationKey, product);
      }
    });

    return combinationsMap;
  }

  getLastAddedProduct() {
    const productsClone = this._products?.slice();
    const sortedProducts = productsClone?.sort((a, b) => {
      return a.createdAt.getTime() - b.createdAt.getTime();
    });
    return sortedProducts?.[sortedProducts.length - 1];
  }

  getEmptyOrderProducts() {
    return (
      this._products?.filter((product) => product.availability === AvailabilityEnum.EMPTY) ?? []
    );
  }

  private updateUpdatedAt() {
    this._updatedAt = new Date();
  }

  private replaceProductToOther(
    productToReplace: OfflineOrderProduct,
    newProduct: OfflineOrderProduct,
  ) {
    this._products = this._products?.map((product) =>
      product === productToReplace ? newProduct : product,
    );
  }

  get code(): number {
    if (!this._code) {
      throw new Error('code is not yet assigned');
    }
    return this._code;
  }

  get id() {
    return this._id;
  }

  get totalPieces() {
    if (!this._products?.length) {
      return 0;
    }

    const allGrids = this._products.flatMap((product) => product.grid);
    return allGrids.reduce((acc, gridItem) => acc + gridItem.quantity, 0);
  }

  get totalItems() {
    if (!this._products?.length) {
      return 0;
    }

    return this._products.length;
  }

  get totalRetail() {
    const total = this._products?.reduce((acc, product) => {
      return acc + product.retailPrice;
    }, 0);
    return total || 0;
  }

  get grossValue() {
    const total = this._products?.reduce((acc, product) => {
      return acc + product.grossValue;
    }, 0);
    return total || 0;
  }

  get subTotal() {
    const total = this._products?.reduce((acc, product) => {
      return acc + product.subTotal;
    }, 0);
    return total || 0;
  }

  get totalValue() {
    const total = this._products?.reduce((acc, product) => {
      return acc + product.subTotal;
    }, 0);
    return total || 0;
  }

  get deliveryQuantity() {
    const deliveryWindows = this._products?.map((product) => product.deliveryWindow?.id) || [];
    const uniqueDeliveryWindows = Array.from(new Set(deliveryWindows));
    return uniqueDeliveryWindows.length;
  }

  get createdAt() {
    return this._createdAt;
  }

  get updatedAt() {
    return this._updatedAt;
  }

  get status() {
    return this._status;
  }

  get brandId() {
    return this._brandId;
  }

  get customer() {
    return this._customer;
  }

  get salesRepresentative() {
    return this._salesRepresentative;
  }

  get seller() {
    return this._seller;
  }

  get priceTable() {
    return this._priceTable;
  }

  get products() {
    return this._products;
  }

  get observation() {
    return this._observation;
  }

  get metadata() {
    return this._metadata;
  }

  get orderType() {
    const availabilities = R.uniq(
      this.products?.map((product) => transformAvailabilityToPreOrder(product.availability)) || [],
    );

    const isBoth = availabilities.length > 1;

    const orderTypeIfOne = isPreOrder(availabilities[0])
      ? OrderType.PRE_ORDER
      : OrderType.READY_FOR_DELIVERY;

    return isBoth ? OrderType.BOTH : orderTypeIfOne;
  }
}
